/* eslint-disable react/prop-types -- this codebase does not use propTypes (see CLAUDE.md). */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { TrashIcon } from "@heroicons/react/24/outline";
import { deleteStore } from "../../api/mutations";
import { useLanguage } from "../../contexts/LanguageContext";
import { getApiErrorMessages } from "../../utils/apiErrorReasons";
import { Modal, ErrorMessages } from "../../components/ui/Modal";
import {
  buttonClass,
  inputClass,
  labelClass,
} from "../../components/ui/styles";
import { hasRunningSubscription } from "./storePayload";

const LOSS_KEYS = ["cards", "customers", "forms", "branches", "team"];

// Deletion is owner-only on the API (authorize_owner) and, as before, offered
// only once no subscription or Stripe trial is running.
export default function DeleteStoreSection({ store, onGoToBilling }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  // An older API without current_user_role keeps the action, as canManageStore does.
  const role = store.current_user_role;
  const isOwner = role !== "admin" && role !== "member";
  const running = hasRunningSubscription(store);
  const storeName = store.name || "";

  const mutation = useMutation({
    mutationFn: deleteStore,
    onSuccess: async () => {
      setOpen(false);
      await queryClient.invalidateQueries({ queryKey: ["stores"] });
      navigate("/dashboard");
    },
  });

  const close = () => {
    if (mutation.isPending) return;
    setOpen(false);
    setConfirmText("");
  };

  const matches = confirmText === storeName && storeName !== "";

  return (
    <section
      aria-labelledby="settings-delete-title"
      className="flex flex-col gap-3.5 rounded-[14px] border border-red-200 bg-white p-6"
    >
      <h2
        id="settings-delete-title"
        className="text-[17px] font-semibold text-red-800"
      >
        {t("settings.delete.title")}
      </h2>
      {running ? (
        <>
          <p className="text-sm leading-relaxed text-gray-700">
            {t("settings.delete.blocked")}
          </p>
          <button
            type="button"
            onClick={onGoToBilling}
            className={`${buttonClass.secondary} self-start`}
          >
            {t("settings.delete.goToBilling")}
          </button>
        </>
      ) : !isOwner ? (
        <p className="text-sm leading-relaxed text-gray-700">
          {t("settings.delete.ownerOnly")}
        </p>
      ) : (
        <>
          <p className="text-sm leading-relaxed text-gray-700">
            {t("settings.delete.lead", { name: storeName })}
          </p>
          <button
            type="button"
            onClick={() => {
              mutation.reset();
              setConfirmText("");
              setOpen(true);
            }}
            className={`${buttonClass.dangerOutline} self-start`}
          >
            <TrashIcon className="h-4 w-4" aria-hidden="true" />
            {t("settings.delete.deleteStore")}
          </button>
        </>
      )}

      <Modal
        open={open}
        onClose={close}
        role="alertdialog"
        size="max-w-[520px]"
        title={t("settings.delete.dialog.title", { name: storeName })}
        footer={
          <>
            <button
              type="button"
              onClick={close}
              disabled={mutation.isPending}
              className={buttonClass.secondary}
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              disabled={!matches || mutation.isPending}
              onClick={() => matches && mutation.mutate({ storeId: store.id })}
              className={buttonClass.danger}
            >
              {mutation.isPending
                ? t("common.deleting")
                : t("settings.delete.deleteStore")}
            </button>
          </>
        }
      >
        <div className="flex flex-col gap-3 text-sm leading-normal text-gray-700">
          <span>{t("settings.delete.dialog.intro")}</span>
          <ul className="m-0 flex list-disc flex-col gap-1 pl-[18px]">
            {LOSS_KEYS.map((key) => (
              <li key={key}>{t(`settings.delete.dialog.losses.${key}`)}</li>
            ))}
          </ul>
          <p className="rounded-[10px] bg-red-50 px-3 py-2.5 text-red-900">
            {t("settings.delete.dialog.wallet")}
          </p>
          <span>{t("settings.delete.dialog.irreversible")}</span>
        </div>
        <label className={labelClass}>
          <span>
            {t("settings.delete.dialog.typeToConfirm", {
              name: <strong>{storeName}</strong>,
            })}
          </span>
          <input
            type="text"
            name="confirm-store-name"
            autoComplete="off"
            spellCheck={false}
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className={`${inputClass} font-normal`}
          />
        </label>
        {mutation.isError && (
          <ErrorMessages
            messages={getApiErrorMessages(
              mutation.error,
              t,
              "settings.delete.error",
            )}
          />
        )}
      </Modal>
    </section>
  );
}
